import { useLocation } from "react-router-dom";
import { StyledFieldsStructure } from "./AccountFieldsStructure.styled"
import { useEffect, useState } from "react";
import { AccountStructure, AccountStructureSchema } from "../../models/accounts-structure";
import { TicketHebType } from "../../models/types";
import { Controller, SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Checkbox, CircularProgress, MenuItem, Select, TextField } from "@mui/material";
import { z } from "zod";
import { ErrorMessage } from "@hookform/error-message";
import { checkInputValue, compareObjects } from "../../helpers/helpers";
import { toast } from 'react-toastify';
import { useErrorBoundary } from "react-error-boundary";
import { handleApiErrors } from "../../services/errorHandling";
import { Params, SelectTable } from "../../models/types";
import { setParams } from "./AccountFieldsStructure.utils";
import { accountsTables, inputTypes } from "./AccountFieldsStructure.data";
import { Table } from "../../models/table";
import ReturnButton from "../../components/return-button/ReturnButton";
import { useAddCustomAccountStructureMutation, useEditAccountStructureMutation, useGetAccountStructureQuery } from "../../redux/api/accounts-structure.api";
import { useGetSystemTablesQuery } from "../../redux/api/system-tables.api";



function AccountFieldsStructure() {

    const location = useLocation();
    const { showBoundary } = useErrorBoundary();

    const params: Params = setParams(location.pathname);

    const { data: systemTables } = useGetSystemTablesQuery();
    const { data: accountStructure } = useGetAccountStructureQuery(params.ticketHebType);

    const [editAccountStructure] = useEditAccountStructureMutation();
    const [addCustomAccountStructure] = useAddCustomAccountStructureMutation();

    const [tables, setTables] = useState<SelectTable[]>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [accountStructureCopy, setAccountStructureCopy] = useState<AccountStructure[]>();


    // account structures form

    const accountStructureForm = useForm<{ structures: AccountStructure[] }>({
        resolver: zodResolver(z.object({ structures: z.array(AccountStructureSchema) })),
        defaultValues: {
            structures: undefined
        }
    });

    const accountStructureFieldsArray = useFieldArray({
        name: 'structures',
        control: accountStructureForm.control
    });



    // ************************************************************************************************************
    //  use effects
    // ************************************************************************************************************

    useEffect(()=>{
        if(accountStructure){
            setAccountStructureCopy(accountStructure);
            accountStructureForm.reset({ structures: accountStructure });
        }
    },[accountStructure]);


    useEffect(()=>{
        if(systemTables){
            setTable(systemTables,accountsTables);
        }
    },[systemTables]);



    useEffect(()=>{
        if(tables && accountStructureCopy){
            setIsLoading(false);
        }
    },[tables, accountStructureCopy]);



    // ************************************************************************************************************
    //  component functions
    // ************************************************************************************************************



    /**
     * combine system and accounts tables to one select table and update the state.
     * @param systemTables 
     * @param accountsTables 
     */
    function setTable(systemTables: Table[], accountsTables: SelectTable[]){

        const selectSystemTables: SelectTable[] = (systemTables ?? []).map(table => ({
            value: table.index,
            name: table.name ?? ""
        }));

        setTables([...selectSystemTables, ...accountsTables]);
    }



    /**
     * Saves structure form data
     */
    function saveTable() {
        accountStructureForm.handleSubmit(onSubmitAccountStructure)();
    }



    /**
     * Saves only the modified structures by comparing them with the last saved copy.
     * Shows a success toast.
     */
    const onSubmitAccountStructure: SubmitHandler<{ structures: AccountStructure[] }> = async (data) => {

        if (accountStructureCopy) {
            try {
                setIsSaving(true);

                for (const [index, structure] of data.structures.entries()) {
                    if (!compareObjects(structure, accountStructureCopy[index])) {
                        await editAccountStructure(structure);
                    }
                };

                toast.success("השינויים נשמרו");
            } 
            
            catch (error) {
                handleApiErrors(error, showBoundary);
            }

            finally {
                setIsSaving(false); // Always reset loading state
            }
        }

    }

    // ************************************************************************************************************


    /**
     * add new account custom structure
     * @param accountHebType 
     */
    async function addCustomField(ticketHebType: TicketHebType){

        try {
            const lastObjectIndex = (accountStructureCopy?.length || 0) - 1;
            const customFieldNumber = (accountStructureCopy?.[lastObjectIndex].custom_field || 0) + 1;

            await addCustomAccountStructure({
                account_type: ticketHebType,
                account_title: "מותאם אישית",
                required: false,
                show_form: true,
                show_search: false,
                show_browse: false,
                field_name: `custom_data.${customFieldNumber - 1}.field_value`,
                field_heb_name: `מותאם אישית ${customFieldNumber}`,
                field_display_name: `מותאם אישית ${customFieldNumber}`,
                field_length: 5,
                field_type: 1,
                field_table: 0,
                custom_field: customFieldNumber,
                editable: true,
                location: 0
            });

        } catch (error) {
            handleApiErrors(error, showBoundary);
        }
    }

    // ************************************************************************************************************



    return (
        <StyledFieldsStructure>
            {isLoading && <CircularProgress color="inherit" sx={{ display: "block", margin: "auto", padding: 4 }} />}
            {!isLoading && (<>
                <div className="head">הגדרת מבנה {params.formType === "search"? "חיפוש" : params.formType === "browse"? "דפדוף" : "טופס"} {params.ticketHebType}</div>
                <div className="table">
                    <table>
                        <thead>
                            <tr>
                                <th>הצגה {params.formType === 'form' ? "בטופס" : params.formType === 'search' ? "בחיפוש" : "בטבלה"}</th>
                                <th>שם שדה</th>
                                <th>שם תצוגה</th>
                                <th>אורך</th>
                                <th>סוג</th>
                                <th>טבלה</th>
                            </tr> 
                        </thead>
                        <tbody>
                            {
                                tables && accountStructureFieldsArray.fields?.map((field, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Controller
                                                control={accountStructureForm.control}
                                                name={params.formType === 'form' ? `structures.${index}.show_form` : params.formType === 'search' ? `structures.${index}.show_search` : `structures.${index}.show_browse`}

                                                render={({ field: { onChange, value } }) => (
                                                    <Checkbox
                                                        disabled={params.formType === 'form' && !!field.required}
                                                        onChange={onChange}
                                                        checked={!!value} />
                                                )}
                                            />
                                        </td>
                                        <td>{field.field_heb_name}</td>
                                        <td>
                                            <Controller
                                                control={accountStructureForm.control}
                                                name={`structures.${index}.field_display_name`}
                                                render={({ field: { onChange, value } }) => (
                                                    <TextField
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value)}
                                                        size="small"
                                                        error={accountStructureForm?.formState?.errors?.structures?.[index]?.field_display_name ? true : false}
                                                        helperText={<ErrorMessage errors={accountStructureForm.formState.errors} name={`structures.${index}.field_display_name`} />}
                                                    />
                                                )}
                                            />
                                        </td>
                                        <td>
                                            <Controller
                                                control={accountStructureForm.control}
                                                name={`structures.${index}.field_length`}
                                                render={({ field: { onChange, value } }) => (
                                                    <TextField
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value)}
                                                        size="small"
                                                        error={accountStructureForm?.formState?.errors?.structures?.[index]?.field_length ? true : false}
                                                        helperText={<ErrorMessage errors={accountStructureForm.formState.errors} name={`structures.${index}.field_length`} />}
                                                    />
                                                )}
                                            />
                                        </td>
                                        <td>
                                            <Controller
                                                control={accountStructureForm.control}
                                                name={`structures.${index}.field_type`}
                                                render={({ field: { onChange, value } }) => (
                                                    <Select
                                                        sx={{ minWidth: 150 }}
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value)}
                                                        size="small"
                                                    >
                                                        {inputTypes.map((obj, index) => (
                                                            <MenuItem key={index} value={obj.value}>{obj.value}. {obj.name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />
                                        </td>
                                        <td>
                                            <Controller
                                                control={accountStructureForm.control}
                                                name={`structures.${index}.field_table`}
                                                render={({ field: { onChange, value } }) => (
                                                    <Select
                                                        sx={{ minWidth: 150 }}
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value)}
                                                        size="small"
                                                    >
                                                        <MenuItem value={0}></MenuItem>
                                                        {tables?.map((obj, index) => (
                                                            <MenuItem key={index} value={obj.value}>{obj.value}. {obj.name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />
                                        </td>
                                    </tr>
                                ))
                            }

                            
                        </tbody>
                    </table>
                </div>
                <div className="buttons">
                    <Button variant='contained' onClick={saveTable} disabled={isSaving}>שמירה</Button>
                    <ReturnButton/>
                    <Button variant="outlined" onClick={()=>{addCustomField(params.ticketHebType)}}>הוספת שורה</Button>

                </div>


            </>)}


            {/* <DevTool control={accountStructureForm.control} /> */}

        </StyledFieldsStructure>
    )
}

export default AccountFieldsStructure